export interface IncognitoCheck {
	is_incognito: boolean;
	/** Каким признаком определен приватный режим - нужно для оценки достоверности по факту эксплуатации */
	incognito_reason: string | null;
}

const MIB = 1024 * 1024;

function isFirefox(): boolean {
	return /Firefox\//.test(navigator.userAgent);
}

function isWebKit(): boolean {
	return /Safari\//.test(navigator.userAgent) && !/Chrome|Chromium|Edg|OPR|YaBrowser/.test(navigator.userAgent);
}

/**
 * localStorage в приватном режиме старых версий Safari бросает исключение при записи
 */
function storageThrows(): boolean {
	try {
		window.localStorage.setItem('smenadc-private-test', '1');
		window.localStorage.removeItem('smenadc-private-test');
		return false;
	} catch {
		return true;
	}
}

/**
 * Квота хранилища в приватном режиме Chromium жестко ограничена (порядка 120 Мб),
 * в обычном режиме она считается от свободного места на диске.
 * Порог считаем от лимита кучи JS - он пропорционален классу устройства.
 */
async function quotaLooksPrivate(): Promise<boolean | null> {
	if (!navigator.storage?.estimate) return null;
	try {
		const { quota } = await navigator.storage.estimate();
		if (!quota) return null;

		const heapLimit = (performance as unknown as { memory?: { jsHeapSizeLimit?: number } }).memory?.jsHeapSizeLimit;
		const limitMib = heapLimit ? (heapLimit / MIB) * 2 : 1024;
		return quota / MIB < limitMib;
	} catch {
		return null;
	}
}

/**
 * Определение приватного режима браузера.
 *
 * Надежного и универсального способа не существует: браузеры целенаправленно
 * закрывают такие возможности, а признаки меняются от версии к версии.
 * Поэтому проверка построена на нескольких независимых сигналах, а результат
 * сохраняется вместе с причиной срабатывания - чтобы оценить точность до включения запрета.
 */
export async function detectIncognito(): Promise<IncognitoCheck> {
	try {
		if (storageThrows()) {
			return { is_incognito: true, incognito_reason: 'localstorage-blocked' };
		}

		// В приватных окнах Firefox отключены service worker'ы
		if (isFirefox() && !('serviceWorker' in navigator) && window.isSecureContext) {
			return { is_incognito: true, incognito_reason: 'firefox-no-service-worker' };
		}

		const quota = await quotaLooksPrivate();
		if (quota === true) {
			return { is_incognito: true, incognito_reason: isWebKit() ? 'webkit-quota' : 'chromium-quota' };
		}

		return { is_incognito: false, incognito_reason: null };
	} catch {
		return { is_incognito: false, incognito_reason: null };
	}
}
